import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useMachine } from '@xstate/react';
import deepEqual from 'fast-deep-equal';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { SingleValue } from 'react-select';
import {
  AsyncCreatableSelect,
  FormRow as Row,
  FormSection as Section,
  Select,
  TextInput,
} from '~/components';
import { COUNTRIES } from '~/constants';
import { API_CALLS } from '~/features/vault/facility/api';
import { emptyFacilityForm } from '~/features/vault/facility/constants';
import { DuplicateFacilityDialog } from '~/features/vault/facility/DuplicateFacilityDialog';
import { facilityMachine } from '~/features/vault/facility/facility.machine';
import { StateOrProvinceInput } from '~/features/vault/facility/StateOrProvinceInput';
import { useCustomForm } from '~/hooks/useCustomForm';
import { facilityFormSchema } from '../../schemas';
import { TrainingFormValues } from '../../types';
import { FacilityFormValues } from './types';
import { WizardFooter } from './WizardFooter';

const TAGS = CT.facility.form;
type FacilityOption = TrainingFormValues['facility'];

const withDefaultFacility = (
  facility: Partial<FacilityOption> = emptyFacilityForm
): FacilityOption => ({
  ...emptyFacilityForm,
  ...facility,
  address: {
    ...emptyFacilityForm.address,
    ...facility.address,
  },
});

interface Props {
  currentPage: number;
  form: Partial<TrainingFormValues>;
  wizardNext: (values: Partial<TrainingFormValues>) => void;
  wizardPrev: (values?: Partial<TrainingFormValues>) => void;
  wizardSubmit: (values: Omit<TrainingFormValues, 'id'>) => void;
  closeWizard: () => void;
}

export const FacilityFormPage = (props: Props) => {
  const { form, wizardNext } = props;

  const methods = useCustomForm<FacilityFormValues>({
    resolver: zodResolver(facilityFormSchema),
    defaultValues: {
      facility: withDefaultFacility(form.facility),
      facilityReference: form.facilityReference,
    },
  });
  const { reset, formState, getValues, setValue } = methods;
  const acceptFacility = (facility: FacilityOption) => {
    const values = {
      facility: withDefaultFacility(facility),
      facilityReference: withDefaultFacility(facility),
    };
    reset(values);
    wizardNext(values);
  };
  const [facilityState, sendFacility] = useMachine(
    facilityMachine.provide({
      actions: {
        notifyParentOnCreation: ({ context }) => acceptFacility(context.form),
        notifyParentOnSelection: ({ context }) => acceptFacility(context.form),
      },
    })
  );
  const busy = facilityState.context.loading || facilityState.context.showDuplicates;
  const nextWithSavedFacility = (values: Partial<TrainingFormValues>) => {
    // The actor rejects repeated submissions while checking/saving. Never pass
    // the unsaved option (empty id) into the next wizard step.
    if (values.facility?.userAdded && !values.facility.value) {
      sendFacility({ type: 'ON_SUBMIT_INLINE', values: withDefaultFacility(values.facility) });
    } else {
      wizardNext(values);
    }
  };

  useEffect(() => {
    if (!deepEqual(formState.defaultValues?.facility, form.facility)) {
      reset({
        ...formState.defaultValues,
        facility: withDefaultFacility(form.facility),
        facilityReference: form.facilityReference,
      });
    }
  }, [form.facility]);

  return (
    <>
      <FormProvider {...methods}>
        <fieldset disabled={busy} className="min-w-0" aria-busy={busy}>
          <Section componentsPerLine={1}>
            <Row>
              <AsyncCreatableSelect
                name={'facility'}
                cyTag={TAGS.nameSelect}
                label={'Facility Name'}
                required
                cacheOptions={true}
                loadOptions={API_CALLS.get}
                noOptionsMessage="No matching facility — keep typing to add your own"
                allowCreateWhileMatching
                disabled={busy}
                onCreateOption={(inputValue: string) => {
                  const label = inputValue.trim();
                  if (!label) return;
                  setValue(
                    'facility',
                    {
                      ...withDefaultFacility(getValues().facility),
                      label,
                      value: '',
                      userAdded: true,
                    },
                    { shouldDirty: true, shouldValidate: true }
                  );
                }}
                onChange={(option: SingleValue<FacilityOption>) => {
                  reset({
                    facility: withDefaultFacility(option ?? emptyFacilityForm),
                    facilityReference: option ? withDefaultFacility(option) : undefined,
                  });
                }}
              />
            </Row>
            <Row>
              <TextInput
                label={'Address Line 1'}
                name={'facility.address.addressLine1'}
                cyTag={TAGS.addressLine1}
                autoComplete={'street address address-line1'}
                required
              />
            </Row>
            <Row>
              <TextInput
                label={'Address Line 2'}
                name={'facility.address.addressLine2'}
                cyTag={TAGS.addressLine2}
                autoComplete={'street address address-line2'}
              />
            </Row>
            <Row>
              <TextInput
                label={'City'}
                name={'facility.address.city'}
                cyTag={TAGS.cityInput}
                autoComplete={'street address city'}
                required
              />
            </Row>
            <Row>
              <StateOrProvinceInput
                name={'facility.address.stateCode'}
                countryFieldName={'facility.address.countryCode'}
                cyTag={TAGS.stateSelect}
                required
              />
            </Row>
            <Row>
              <TextInput
                label={'Postal Code'}
                name={'facility.address.postalCode'}
                cyTag={TAGS.postalInput}
                autoComplete={'postal-code'}
                required
              />
            </Row>
            <Row>
              <Select
                name={'facility.address.countryCode'}
                label={'Country'}
                cyTag={TAGS.countrySelect}
                options={COUNTRIES}
                required
              />
            </Row>
            {facilityState.context.inlineError && (
              <p role="alert" className="text-sm text-red-600">
                {facilityState.context.inlineError}
              </p>
            )}
            <WizardFooter {...props} wizardNext={nextWithSavedFacility} busy={busy} />
          </Section>
        </fieldset>
      </FormProvider>
      <DuplicateFacilityDialog
        open={facilityState.context.showDuplicates}
        duplicates={facilityState.context.duplicates}
        pendingName={facilityState.context.pendingValues.label}
        onSelectExisting={(facility) => sendFacility({ type: 'ON_SELECT_EXISTING', facility })}
        onConfirmCreate={() => sendFacility({ type: 'ON_CONFIRM_CREATE' })}
        onBack={() => sendFacility({ type: 'ON_BACK_TO_FORM' })}
      />
    </>
  );
};
